import React, { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  GetApp,
  DragIndicator,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';

// Set up PDF.js worker
// IMPORTANT: Make sure pdf.worker.min.js is in your public/ folder
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface PDFPage {
  id: string;
  fileIndex: number;
  pageIndex: number;
  buffer: Uint8Array;
  fileName: string;
  numPages: number;
}

const PDFBreaker: React.FC = () => {
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfPages, setPdfPages] = useState<PDFPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleFileDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setError(null);
    
    const files = Array.from(e.dataTransfer.files).filter(file => file.type === 'application/pdf');
    
    if (files.length === 0) {
      setError('Por favor, sube archivos PDF válidos');
      return;
    }

    setLoading(true);
    try {
      const newPages: PDFPage[] = [];
      
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const numPages = pdf.getPageCount();
        
        // For single file, add all pages
        // For multiple files, add only first page of each
        const pagesToAdd = files.length === 1 ? numPages : 1;
        
        
        for (let pageIndex = 0; pageIndex < pagesToAdd; pageIndex++) {
          // Create a new PDF with just this page
          const singlePagePdf = await PDFDocument.create();
          const [copiedPage] = await singlePagePdf.copyPages(pdf, [pageIndex]);
          singlePagePdf.addPage(copiedPage);
          const singlePageBuffer = await singlePagePdf.save();
          newPages.push({
            id: `${fileIndex}-${pageIndex}`,
            fileIndex,
            pageIndex,
            buffer: new Uint8Array(singlePageBuffer),
            fileName: file.name,
            numPages,
          });
        }
      }
      
      setPdfFiles(prev => [...prev, ...files]);
      setPdfPages(prev => [...prev, ...newPages]);
    } catch (error) {
      setError('Error al procesar los archivos PDF');
      console.error('Error processing PDF files:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFileDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDragStart = (e: React.DragEvent, pageId: string) => {
    setDraggedItem(pageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetPageId: string) => {
    try {
      e.preventDefault();
      if (!draggedItem || draggedItem === targetPageId) return;
      setPdfPages(prev => {
        const draggedIndex = prev.findIndex(page => page.id === draggedItem);
        const targetIndex = prev.findIndex(page => page.id === targetPageId);
        if (draggedIndex === -1 || targetIndex === -1) return prev;
        const newPages = [...prev];
        const [draggedPage] = newPages.splice(draggedIndex, 1);
        newPages.splice(targetIndex, 0, draggedPage);
        return newPages;
      });
      setDraggedItem(null);
    } catch (err: any) {
      setError('Error al reordenar páginas: ' + (err?.message || err));
    }
  };

  const handleDeletePage = (pageId: string) => {
    setPdfPages(prev => prev.filter(page => page.id !== pageId));
    
    // Remove file if no more pages from that file
    const remainingPages = pdfPages.filter(page => page.id !== pageId);
    const remainingFileIndices = new Set(remainingPages.map(page => page.fileIndex));
    setPdfFiles(prev => prev.filter((_, index) => remainingFileIndices.has(index)));
  };

  const handleDownload = async () => {
    if (pdfPages.length === 0) return;
    setLoading(true);
    try {
      const mergedPdf = await PDFDocument.create();
      for (const page of pdfPages) {
        try {
          const pdf = await PDFDocument.load(page.buffer);
          const [copiedPage] = await mergedPdf.copyPages(pdf, [0]);
          mergedPdf.addPage(copiedPage);
        } catch (err: any) {
          setError('Error al procesar una página para descarga: ' + (err?.message || err));
        }
      }
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'merged-document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setError('Error al generar el archivo PDF: ' + (error?.message || error));
      console.error('Error generating PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  // Error boundary for PDF preview
  function PDFPreview({ page }: { page: PDFPage }) {
    const [localError, setLocalError] = useState<string | null>(null);
    return (
      <>
        {localError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Error al mostrar la página: {localError}
          </Alert>
        )}
        <Box sx={{ 
          height: 200, 
          overflow: 'hidden', 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          <Document 
            file={new Blob([page.buffer], { type: 'application/pdf' })}
            onLoadError={err => setLocalError(err.message)}
            onSourceError={err => setLocalError(err.message)}
            error={<Alert severity="error">Error al cargar el PDF</Alert>}
            loading={<CircularProgress size={24} />}
          >
            <Page
              pageNumber={1}
              width={150}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onRenderError={err => setLocalError(err.message)}
              error={<Alert severity="error">Error al renderizar la página</Alert>}
              loading={<CircularProgress size={24} />}
            />
          </Document>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography variant="h4" gutterBottom>
        PDF Breaker
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Sube archivos PDF para reorganizar sus páginas. Arrastra y suelta para reordenar, elimina páginas no deseadas y descarga el resultado final.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {pdfPages.length === 0 && (
        <Paper
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            mb: 3,
            backgroundColor: '#fafafa',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
          onDrop={handleFileDrop}
          onDragOver={handleFileDragOver}
        >
          <CloudUpload sx={{ fontSize: 64, color: '#666', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Arrastra y suelta archivos PDF aquí
          </Typography>
          <Typography variant="body2" color="textSecondary">
            o haz clic para seleccionar archivos
          </Typography>
          <input
            type="file"
            accept=".pdf"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) {
                const dataTransfer = new DataTransfer();
                files.forEach(file => dataTransfer.items.add(file));
                const event = new DragEvent('drop', { dataTransfer });
                handleFileDrop(event as any);
              }
            }}
            id="pdf-upload"
          />
          <label htmlFor="pdf-upload">
            <Button
              variant="contained"
              component="span"
              sx={{ mt: 2 }}
            >
              Seleccionar PDFs
            </Button>
          </label>
        </Paper>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {pdfPages.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Páginas ({pdfPages.length})
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            mb: 3,
            '& > *': {
              flex: '1 1 250px',
              maxWidth: '300px',
              minWidth: '250px'
            }
          }}>
            {pdfPages.map((page, index) => (
              <Paper
                key={page.id}
                sx={{
                  p: 1,
                  cursor: 'grab',
                  opacity: draggedItem === page.id ? 0.5 : 1,
                  border: draggedItem === page.id ? '2px dashed #1976d2' : '1px solid #e0e0e0',
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, page.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, page.id)}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Página {page.pageIndex + 1}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePage(page.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                      <DragIndicator fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
                    </Box>
                  </Box>
                  
                  <PDFPreview page={page} />
                  
                  <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                    {page.fileName}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleDownload}
              disabled={loading || pdfPages.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <GetApp />}
            >
              {loading ? 'Generando...' : 'Descargar PDF'}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default PDFBreaker; 